"use client";

import type { ProductDetails } from "@ellaandil/formatters";
import { useMemo, useState } from "react";
import { Swatch } from "@/components/ui/Swatch";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";
import { Link as NextLink, productCanonicalPath } from "@/i18n/routing";

type Variant = ProductDetails["variants"][number];

export interface VariantSelectorProps {
	currentVariant: Variant;
	variants: Variant[];
}

export const VariantSelector = ({ currentVariant, variants }: VariantSelectorProps) => {
	const t = useTranslations();
	const [hoveredVariantName, setHoveredVariantName] = useState<string | null>(null);
	// Merge current variant into the list and sort by displayItemId for stable order
	const allVariants = useMemo(() => {
		const hasCurrentInList = variants.some((v) => v.displayItemId === currentVariant.displayItemId);
		const merged = hasCurrentInList ? variants : [currentVariant, ...variants];
		return merged.toSorted((a, b) => a.displayItemId.localeCompare(b.displayItemId));
	}, [variants, currentVariant]);

	if (allVariants.length === 0) return null;

	const isSingle = allVariants.length === 1;

	return (
		<div className="flex flex-col gap-grid-component-spacing-2xs">
			<div className="flex gap-2 items-start">
				<Typography variant="label-sm-reg" className="text-text-dark-text-2">
					{isSingle ? t("Color") : t("Select color")}
				</Typography>
				<Typography variant="label-sm-reg" className="text-text-dark-text-1">
					{hoveredVariantName ?? currentVariant.variantName}
				</Typography>
			</div>

			<div className="flex flex-wrap gap-grid-component-spacing-4xs">
				{allVariants.map((variant) => {
					const isActive = variant.displayItemId === currentVariant.displayItemId;
					const firstImage = variant.media?.[0];
					const href = productCanonicalPath(variant.uri);

					return (
						<Swatch
							key={variant.displayItemId}
							as={isSingle ? "span" : NextLink}
							href={isSingle ? undefined : href}
							className={isSingle ? "cursor-default" : undefined}
							hex={variant.swatch?.hex}
							image={firstImage ? { href: firstImage.href, alt: firstImage.alt || "" } : undefined}
							active={isActive}
							aria-label={`${variant.name} – ${variant.variantName}`}
							aria-current={isActive ? "true" : undefined}
							onMouseEnter={isSingle ? undefined : () => setHoveredVariantName(variant.variantName)}
							onMouseLeave={isSingle ? undefined : () => setHoveredVariantName(null)}
							onFocus={isSingle ? undefined : () => setHoveredVariantName(variant.variantName)}
							onBlur={isSingle ? undefined : () => setHoveredVariantName(null)}
						/>
					);
				})}
			</div>
		</div>
	);
};
